import React, { useState, useEffect, useRef } from 'react';

// suffixes are K, K+, +, M, M+
const StatsSection = ({
                          stats = [
                              { number: 100, label: "Gardens Designed", suffix: "+" },
                              { number: 50, label: "Plant Varieties", suffix: "+" },
                              { number: 20, label: "Happy Gardeners", suffix: "+" },
                              { number: 500, label: "Plants Grown", suffix: "+" }
                          ],
                          className = ""
                      }) => {
    const [animated, setAnimated] = useState(false);
    const [animatedNumbers, setAnimatedNumbers] = useState(stats.map(() => 0));
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !animated) {
                        setAnimated(true);
                        animateNumbers();
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, [animated]);

    const animateNumbers = () => {
        stats.forEach((stat, index) => {
            let current = 0;
            const target = stat.number;
            const increment = target / 50;

            const timer = setInterval(() => {
                current += increment;
                if (current >= target) {
                    current = target;
                    clearInterval(timer);
                }

                setAnimatedNumbers(prev => {
                    const newNumbers = [...prev];
                    newNumbers[index] = Math.floor(current);
                    return newNumbers;
                });
            }, 30);
        });
    };

    const formatNumber = (number, suffix) => {
        if (suffix.includes('K')) {
            return Math.floor(number / 1000) + 'K+';
        } else if (suffix.includes('M')) {
            return Math.floor(number / 1000000) + 'M+';
        } else {
            return number + '+';
        }
    };

    return (
        <section ref={sectionRef} className={`stats-section ${className}`}>
            <style jsx>{`
        .stats-section {
          background: linear-gradient(135deg, #2d5a27, #4a7c59);
          color: white;
          padding: 4rem 2rem;
          text-align: center;
          //border-radius: 20px;
          margin: 2rem 0 0 0;
        }

        .stats-container {
          max-width: 1000px;
          margin: 0 auto;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
        }

        .stat-item {
          padding: 1rem;
          transition: transform 0.3s ease;
        }

        .stat-item:hover {
          transform: translateY(-5px);
        }

        .stat-number {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          background: linear-gradient(45deg, #ff6b35, #f7931e);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          transition: all 0.3s ease;
        }

        .stat-label {
          font-size: 1.1rem;
          opacity: 0.9;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stats-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 3rem;
          color: white;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        }

        .stats-subtitle {
          font-size: 1.2rem;
          opacity: 0.9;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        /* Pulse animation for numbers */
        .stat-number.animating {
          animation: pulse 0.1s ease-in-out;
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        /* Background pattern */
        .stats-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="75" cy="75" r="1.5" fill="rgba(255,255,255,0.08)"/><circle cx="50" cy="10" r="0.5" fill="rgba(255,255,255,0.06)"/><circle cx="10" cy="60" r="1" fill="rgba(255,255,255,0.04)"/><circle cx="90" cy="40" r="0.8" fill="rgba(255,255,255,0.07)"/></svg>');
          opacity: 0.3;
          pointer-events: none;
        }

        .stats-section {
          position: relative;
        }

        @media (max-width: 768px) {
          .stats-section {
            padding: 3rem 1rem;
          }
          
          .stats-title {
            font-size: 2rem;
          }
          
          .stats-subtitle {
            font-size: 1.1rem;
          }
          
          .stat-number {
            font-size: 2.5rem;
          }
          
          .stat-label {
            font-size: 1rem;
          }
          
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 1.5rem;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .stat-number {
            font-size: 2rem;
          }
        }
      `}</style>

            <div className="stats-container">
                <div className="stats-grid">
                    {stats.map((stat, index) => (
                        <div key={index} className="stat-item">
                            <div className="stat-number">
                                {formatNumber(animatedNumbers[index], stat.suffix)}
                            </div>
                            <div className="stat-label">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// You can also export a version with title and subtitle
export const StatsWithTitle = ({
                                   title = "Growing Together",
                                   subtitle = "Join thousands of gardeners who've transformed their spaces",
                                   stats,
                                   className = ""
                               }) => {
    return (
        <section className={`stats-section ${className}`}>
            <style jsx>{`
        .stats-section {
          background: linear-gradient(135deg, #2d5a27, #4a7c59);
          color: white;
          padding: 4rem 2rem;
          text-align: center;
          border-radius: 20px;
          margin: 2rem 0;
          position: relative;
        }

        .stats-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="75" cy="75" r="1.5" fill="rgba(255,255,255,0.08)"/><circle cx="50" cy="10" r="0.5" fill="rgba(255,255,255,0.06)"/><circle cx="10" cy="60" r="1" fill="rgba(255,255,255,0.04)"/><circle cx="90" cy="40" r="0.8" fill="rgba(255,255,255,0.07)"/></svg>');
          opacity: 0.3;
          pointer-events: none;
        }

        .stats-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: white;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
          position: relative;
          z-index: 1;
        }

        .stats-subtitle {
          font-size: 1.2rem;
          opacity: 0.9;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          position: relative;
          z-index: 1;
        }

        @media (max-width: 768px) {
          .stats-title {
            font-size: 2rem;
          }
          
          .stats-subtitle {
            font-size: 1.1rem;
          }
        }
      `}</style>

            <div className="stats-container">
                <h2 className="stats-title">{title}</h2>
                <p className="stats-subtitle">{subtitle}</p>
                <StatsSection stats={stats} />
            </div>
        </section>
    );
};

export default StatsSection;